import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_MY_QUOTATIONS } from '../../apollo/queries/quotationQueries';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';
import '../css/QuotationHistoryPage.css';

const QuotationHistoryPageContent = () => {
    const navigate = useNavigate();
    
    // GraphQL 쿼리로 실제 견적 데이터 가져오기
    const { data, loading, error } = useQuery(GET_MY_QUOTATIONS, {
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network'
    });

    // 검색 필터 상태
    const [searchFilters, setSearchFilters] = useState({
        customerOrderNumber: '',
        quotationNumber: '',
        productName: '',
        status: 'all',
        dateFilter: 'recent3months', // 'recent3months' | 'custom'
        startDate: '',
        endDate: ''
    });

    const handleFilterChange = (field, value) => {
        setSearchFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 날짜 필터링 함수
    const filterQuotationsByDate = (quotations) => {
        if (searchFilters.dateFilter === 'recent3months') {
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            return quotations.filter(q => new Date(q.createdAt) >= threeMonthsAgo);
        } else if (searchFilters.dateFilter === 'custom' && searchFilters.startDate && searchFilters.endDate) {
            const startDate = new Date(searchFilters.startDate);
            const endDate = new Date(searchFilters.endDate);
            endDate.setHours(23, 59, 59, 999); // 종료일의 끝까지 포함
            return quotations.filter(q => {
                const qDate = new Date(q.createdAt);
                return qDate >= startDate && qDate <= endDate;
            });
        }
        return quotations;
    };

    const handleCheckboxChange = (quotationId) => {
        // 견적 체크박스 로직 (필요시 구현)
    };

    // 이미지 파싱 함수
    const parseProductImage = (images) => {
        if (!images) return null;
        
        try {
            if (typeof images === 'string') {
                const cleanedImages = images.trim();
                if (cleanedImages && cleanedImages !== '[]' && cleanedImages !== '') {
                    if (cleanedImages.startsWith('[')) {
                        const parsedImages = JSON.parse(cleanedImages);
                        if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                            return parsedImages[0];
                        }
                    } else if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                        return cleanedImages;
                    }
                }
            } else if (Array.isArray(images) && images.length > 0) {
                return images[0];
            }
        } catch (e) {
            // JSON 파싱 실패 시 무시
        }
        return null;
    };

    // 견적 데이터 처리 및 필터링
    const rawQuotations = data?.myQuotations || [];
    
    // 날짜 필터링 적용
    const dateFilteredQuotations = filterQuotationsByDate(rawQuotations);
    
    // 다른 필터들 적용
    const quotations = dateFilteredQuotations.filter(quotation => {
        const matchesOrderNumber = !searchFilters.customerOrderNumber || 
            quotation.customerOrderNumber?.toLowerCase().includes(searchFilters.customerOrderNumber.toLowerCase());
        const matchesQuotationNumber = !searchFilters.quotationNumber || 
            quotation.id.toString().includes(searchFilters.quotationNumber);
        const matchesProductName = !searchFilters.productName || 
            quotation.quotationItems?.some(item => 
                item.product?.name?.toLowerCase().includes(searchFilters.productName.toLowerCase())
            );
        const matchesStatus = searchFilters.status === 'all' || quotation.status === searchFilters.status;
        
        return matchesOrderNumber && matchesQuotationNumber && matchesProductName && matchesStatus;
    });

    // 로딩 상태
    if (loading) {
        return (
            <div className="quotation-history-page">
                <div className="qhp-container">
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <div>견적 이력을 불러오는 중입니다...</div>
                    </div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className="quotation-history-page">
                <div className="qhp-container">
                    <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
                        <div>견적 이력을 불러오는 중 오류가 발생했습니다: {error.message}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="quotation-history-page">
            <div className="qhp-container">
                <div className="qhp-breadcrumb">
                    <span>홈</span> &gt; <span>내 정보</span> &gt; <span className="current">견적 이력</span>
                </div>
                <h1 className="qhp-title">견적 이력</h1>
                <p className="qhp-description">
                    견적이력을 확인할 수 있습니다. 견적 이력은 아래에서 검색조건을 설정할 수 있습니다.
                </p>

                <div className="qhp-search-filters">
                    <div className="filter-row">
                        <label>고객 주문번호</label>
                        <input 
                            type="text" 
                            placeholder="고객 주문 번호를 입력해 주세요." 
                            value={searchFilters.customerOrderNumber}
                            onChange={(e) => handleFilterChange('customerOrderNumber', e.target.value)}
                        />
                    </div>
                    <div className="filter-row">
                        <label>견적 번호</label>
                        <input 
                            type="text" 
                            placeholder="견적 번호를 입력해 주세요." 
                            value={searchFilters.quotationNumber}
                            onChange={(e) => handleFilterChange('quotationNumber', e.target.value)}
                        />
                    </div>
                    <div className="filter-row">
                        <label>견적일</label>
                        <div className="date-filter">
                            <button 
                                className={`date-button ${searchFilters.dateFilter === 'recent3months' ? 'active' : ''}`}
                                onClick={() => handleFilterChange('dateFilter', 'recent3months')}
                            >
                                최근 3개월
                            </button>
                            <button 
                                className={`date-button ${searchFilters.dateFilter === 'custom' ? 'active' : ''}`}
                                onClick={() => handleFilterChange('dateFilter', 'custom')}
                            >
                                날짜 지정
                            </button>
                            {searchFilters.dateFilter === 'custom' && (
                                <div className="date-range-picker">
                                    <input
                                        type="date"
                                        value={searchFilters.startDate}
                                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                        placeholder="시작일"
                                    />
                                    <span>~</span>
                                    <input
                                        type="date"
                                        value={searchFilters.endDate}
                                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                        placeholder="종료일"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="filter-row">
                        <label>제품 명</label>
                        <input 
                            type="text" 
                            placeholder="제품명을 입력해주세요." 
                            value={searchFilters.productName}
                            onChange={(e) => handleFilterChange('productName', e.target.value)}
                        />
                    </div>
                    <div className="filter-row">
                        <label>견적 상황</label>
                        <select 
                            value={searchFilters.status} 
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="all">전체</option>
                            <option value="PENDING">대기 중</option>
                            <option value="COMPLETED">완료</option>
                            <option value="CANCELLED">취소됨</option>
                        </select>
                    </div>
                </div>

                <div className="qhp-quote-list">
                    {quotations.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <div style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
                                견적 이력이 없습니다.
                            </div>
                        </div>
                    ) : (
                        quotations.map(quotation => (
                            <div key={quotation.id} className="quotation-container">
                                <div className="quotation-header">
                                    <input type="checkbox" onChange={() => handleCheckboxChange(quotation.id)} />
                                    <div className="quotation-meta">
                                        <span className="quotation-id">견적번호: {quotation.id}</span>
                                        <span className="quotation-date">견적일: {new Date(quotation.createdAt).toLocaleDateString('ko-KR')}</span>
                                        <span className="quotation-status">상태: {
                                            quotation.status === 'PENDING' ? '견적 대기' : 
                                            quotation.status === 'COMPLETED' ? '견적 완료' : 
                                            quotation.status === 'CANCELLED' ? '취소됨' : quotation.status
                                        }</span>
                                    </div>
                                </div>
                                <div className="quotation-items">
                                    {quotation.quotationItems && quotation.quotationItems.length > 0 ? (
                                        quotation.quotationItems.map(item => {
                                            const imageUrl = parseProductImage(item.product.images);

                                            return (
                                                <div key={item.id} className="quotation-item">
                                                    <div className="item-info">
                                                        <ImageWithPlaceholder 
                                                            src={imageUrl} 
                                                            alt={item.product.name} 
                                                            className="item-image" 
                                                        />
                                                        <div className="item-details">
                                                            <p className="item-name">{item.product.name}</p>
                                                            <p className="item-spec">
                                                                {item.product.description || '상세 정보 없음'}
                                                            </p>
                                                            {item.product.mainCategory && (
                                                                <p className="item-category">
                                                                    카테고리: {item.product.mainCategory.name}
                                                                    {item.product.subCategory && ` > ${item.product.subCategory.name}`}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="item-quantity" data-label="수량">{item.quantity}</div>
                                                    <div className="item-delivery" data-label="납기일">
                                                        {quotation.updatedAt ? new Date(quotation.updatedAt).toLocaleDateString('ko-KR') : '미정'}
                                                    </div>
                                                    <div className="item-status" data-label="상태">
                                                        <p>{quotation.status === 'PENDING' ? '견적 대기' : quotation.status === 'COMPLETED' ? '견적 완료' : '처리됨'}</p>
                                                        <Link to={`/quotation-detail?id=${quotation.id}`}>견적 상세보기</Link>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="quotation-item">
                                            <div className="item-info">
                                                <div className="item-details">
                                                    <p className="item-name">주문 제작 견적 요청</p>
                                                    <p className="item-spec">{quotation.message || '상세 내용 없음'}</p>
                                                </div>
                                            </div>
                                            <div className="item-quantity" data-label="수량">-</div>
                                            <div className="item-delivery" data-label="납기일">
                                                {quotation.updatedAt ? new Date(quotation.updatedAt).toLocaleDateString('ko-KR') : '미정'}
                                            </div>
                                            <div className="item-status" data-label="상태">
                                                <p>{quotation.status === 'PENDING' ? '견적 대기' : quotation.status === 'COMPLETED' ? '견적 완료' : '처리됨'}</p>
                                                <Link to={`/quotation-detail?id=${quotation.id}`}>견적 상세보기</Link>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="qhp-bottom-actions">
                    <button className="order-guide-button" onClick={() => navigate('/order-guide')}>해당 견적 발주 안내</button>
                    <button className="delete-button">해당 견적 삭제</button>
                </div>
            </div>
        </div>
    );
};

const QuotationHistoryPage = () => {
    return (
        <QuotationHistoryPageContent />
    );
};

export default QuotationHistoryPage; 